import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Folder, LayoutGrid } from 'lucide-react';
import { Card } from '../ui/card';
import { FolderListItem } from '../../services/api/folderService';
import { PinButton } from './PinButton';
import { stripHtml } from './descriptionUtils';
import { folderItemCount } from './folderCounts';
import { CategoryChip } from '../ui-components/category/CategoryChip';

interface FolderCardProps {
    folder: FolderListItem;
    onTogglePin?: (folderId: string, nextIsPinned: boolean) => void;
    pinDisabled?: boolean;
}

export const FolderCard = ({
    folder,
    onTogglePin,
    pinDisabled,
}: FolderCardProps) => {
    const { t } = useTranslation();
    const description = stripHtml(folder.description);
    const itemCount = folderItemCount(folder);
    const pinButton = onTogglePin ? (
        <PinButton
            isPinned={folder.isPinned}
            onToggle={() => onTogglePin(folder._id, !folder.isPinned)}
            disabled={pinDisabled}
        />
    ) : null;

    return (
        <Link to={`/group/${folder._id}`} className="block h-full">
            <Card className="flex h-full flex-col gap-2 border-subtle p-4 shadow-none transition-colors hover:bg-muted">
                <div className="flex min-h-6 items-center justify-between gap-2">
                    {folder.category && (
                        <CategoryChip category={folder.category} />
                    )}
                    <span className="ml-auto">{pinButton}</span>
                </div>
                <h3 className="break-words text-sm font-semibold leading-5 text-foreground">
                    {folder.title}
                </h3>
                {description && (
                    <p className="line-clamp-2 text-xs font-medium leading-5 text-subtle-foreground">
                        {description}
                    </p>
                )}
                <div className="mt-auto flex items-center gap-1.5 border-t border-subtle pt-3 text-xs font-medium text-subtle-foreground">
                    {folder.subFolderCount > 0 && (
                        <span className="flex items-center gap-1">
                            <Folder className="h-3 w-3" aria-hidden="true" />
                            {t('folders.overview.counts.subFolders', {
                                count: folder.subFolderCount,
                            })}
                        </span>
                    )}
                    {folder.subFolderCount > 0 && itemCount > 0 && (
                        <span aria-hidden="true">·</span>
                    )}
                    {itemCount > 0 && (
                        <span className="flex items-center gap-1">
                            <LayoutGrid
                                className="h-3 w-3"
                                aria-hidden="true"
                            />
                            {t('folders.overview.counts.items', {
                                count: itemCount,
                            })}
                        </span>
                    )}
                    {folder.subFolderCount === 0 && itemCount === 0 && (
                        <span>{t('folders.overview.counts.empty')}</span>
                    )}
                </div>
            </Card>
        </Link>
    );
};
